import { TFunction } from 'i18next'
import { z } from 'zod'

import type { Service } from '@/api/services'

import {
  NO_TRAINING_TYPE_CATEGORIES,
  SERVICE_DESCRIPTION_MAX_LENGTH,
  SERVICE_NAME_MAX_LENGTH,
} from '../services.constants'
import { getBookingTypeRef } from '../services.utils'

export const SERVICE_IMAGE_PATH = 'chitown-default'

type ServiceT = TFunction<['organisation', 'common']>

export const makeServiceFormSchema = (t: ServiceT) => {
  const maxLength = (max: number) => t('organisation:servicesPage.validation.maxLength', { max })
  const text = (max: number) =>
    z.string().trim().min(1, t('common:validation.required')).max(max, maxLength(max))

  return z
    .object({
      active: z.boolean(),
      activeType: z.boolean(),
      bookingType: z.number().nullable(),
      category: z.string().min(1, t('common:validation.required')),
      color: z.string(),
      description: text(SERVICE_DESCRIPTION_MAX_LENGTH),
      icon: z.string(),
      image: z.array(z.instanceof(File)).optional(),
      imageInCategory: z.array(z.instanceof(File)).optional(),
      internalName: text(SERVICE_NAME_MAX_LENGTH),
      name: text(SERVICE_NAME_MAX_LENGTH),
    })
    .superRefine((data, ctx) => {
      // Note: the BE creates a training type only for a service with both an icon and a color
      if (NO_TRAINING_TYPE_CATEGORIES.includes(data.category)) return

      if (!data.icon) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:servicesPage.validation.iconRequired'),
          path: ['icon'],
        })
      }
      if (!data.color) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:servicesPage.validation.colorRequired'),
          path: ['color'],
        })
      }
    })
}

export type ServiceFormData = z.infer<ReturnType<typeof makeServiceFormSchema>>

interface ServiceDefaultsOptions {
  category?: string
  duplicate?: boolean
}

export const getServiceFormDefaults = (
  service?: Service,
  { category = '', duplicate = false }: ServiceDefaultsOptions = {},
): ServiceFormData => ({
  active: service?.active ?? true,
  activeType: service?.activeType ?? true,
  bookingType: service ? (getBookingTypeRef(service)?.id ?? null) : null,
  category: service?.category ?? category,
  color: service?.color ?? '',
  description: service?.description ?? '',
  icon: service?.icon ?? '',
  image: [],
  imageInCategory: [],
  // Note: internal names are unique, so a duplicate starts without one
  internalName: duplicate ? '' : (service?.internalName ?? ''),
  name: service?.name ?? '',
})
